import { Injectable, NgZone } from '@angular/core';
import { Keyboard, KeyboardResize, KeyboardInfo } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

export interface KeyboardConfig {
  containerId: string;
  inputId: string;
  transform: string;
  /** When true, lift amount is computed from keyboard height and focused field position */
  useViewportLift?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class KeyboardService {
  private keyboardShowListener: PluginListenerHandle | null = null;
  private keyboardHideListener: PluginListenerHandle | null = null;
  private isInitialized = false;
  private isKeyboardAvailable = false;
  private configs: KeyboardConfig[] = [];
  private activeTransforms = new Map<string, string>();
  private keyboardHeight = 0;
  /** Used when switching fields: keyboard stays open but WillShow may not re-fire with height */
  private lastKeyboardHeight = 0;
  /** Cancels spurious hide while jumping between inputs */
  private pendingHideClear: ReturnType<typeof setTimeout> | null = null;
  private visualViewportResizeListener: (() => void) | null = null;
  private visualViewportRaf: number | null = null;
  /** Cancels stale deferred lifts when focus moves quickly between inputs */
  private liftScheduleGeneration = 0;
  private pendingLiftTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(private ngZone: NgZone) {}

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('Keyboard')) {
      this.isInitialized = true;
      return;
    }

    this.isKeyboardAvailable = true;

    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
      await Keyboard.setAccessoryBarVisible({ isVisible: true });

      document.addEventListener(
        'focusin',
        (event: FocusEvent) => {
          const target = event.target as HTMLElement | null;
          if (!target) {
            return;
          }
          if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA'
          ) {
            this.liftForFocusedField(target);
          }
        },
        true
      );

      if (typeof window !== 'undefined' && window.visualViewport) {
        this.visualViewportResizeListener = () => {
          if (this.visualViewportRaf !== null) {
            return;
          }
          this.visualViewportRaf = requestAnimationFrame(() => {
            this.visualViewportRaf = null;
            const ae = document.activeElement as HTMLElement | null;
            if (
              ae &&
              (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')
            ) {
              // Single apply — avoid chaining with focusin (prevents “stacked” lifts)
              this.ngZone.run(() => this.checkAndApplyTransform(ae));
            }
          });
        };
        window.visualViewport.addEventListener(
          'resize',
          this.visualViewportResizeListener
        );
        window.visualViewport.addEventListener(
          'scroll',
          this.visualViewportResizeListener
        );
      }

      this.keyboardShowListener = await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        this.handleKeyboardShow(info);
      });

      this.keyboardHideListener = await Keyboard.addListener('keyboardWillHide', () => {
        this.handleKeyboardHide();
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('KeyboardService initialization error:', error);
    }
  }

  setConfigs(configs: KeyboardConfig[]): void {
    this.configs = configs;
  }

  addConfig(config: KeyboardConfig): void {
    this.configs.push(config);
  }

  removeConfig(inputId: string): void {
    this.configs = this.configs.filter(c => c.inputId !== inputId);
  }

  private findMatchingConfig(input: HTMLElement): KeyboardConfig | undefined {
    const inputId = input.id || '';
    const exactCandidates = this.configs.filter((c) => c.inputId === inputId);
    for (const c of exactCandidates) {
      const el = document.getElementById(c.containerId);
      if (el?.contains(input)) {
        return c;
      }
    }
    return this.configs.find((c) => {
      if (c.inputId !== '*') {
        return false;
      }
      const container = document.getElementById(c.containerId);
      return container ? container.contains(input) : false;
    });
  }

  private effectiveKeyboardHeight(): number {
    if (this.keyboardHeight > 0) {
      return this.keyboardHeight;
    }
    if (this.lastKeyboardHeight > 0) {
      return this.lastKeyboardHeight;
    }
    if (typeof window !== 'undefined' && window.visualViewport) {
      const vv = window.visualViewport;
      const inferred = window.innerHeight - vv.height - vv.offsetTop;
      if (inferred > 80) {
        return Math.round(inferred);
      }
    }
    return 0;
  }

  private applyViewportLift(input: HTMLElement, container: HTMLElement): void {
    const kh = this.effectiveKeyboardHeight();
    if (!kh) {
      return;
    }
    const rect = input.getBoundingClientRect();
    const viewport_h = window.innerHeight;
    const keyboard_top = viewport_h - kh;
    // Clearance above keyboard: keeps form / focused field from sitting on the keyboard edge
    const padding_above_keyboard = 48;
    const extra_visual_gap = 20;
    const safe_bottom =
      keyboard_top - padding_above_keyboard - extra_visual_gap;
    let lift = Math.max(0, rect.bottom - safe_bottom);
    // Taller sheets (e.g. onboarding sign-up) need more lift headroom
    const max_lift = Math.min(viewport_h * 0.75, kh + 140);
    lift = Math.min(lift, max_lift);
    const transform = `translate3d(0, -${Math.round(lift)}px, 0)`;
    container.style.transform = transform;
    container.style.transition = 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    this.activeTransforms.set(container.id, transform);
  }

  private clearPendingLiftSchedules(): void {
    this.pendingLiftTimeouts.forEach((t) => clearTimeout(t));
    this.pendingLiftTimeouts = [];
  }

  /**
   * Recompute lift after focus / layout settle. Cancels previous deferred runs when focus moves fast.
   */
  private liftForFocusedField(target: HTMLElement): void {
    this.clearPendingLiftSchedules();
    const gen = ++this.liftScheduleGeneration;
    const apply = () => {
      if (gen !== this.liftScheduleGeneration) {
        return;
      }
      this.checkAndApplyTransform(target);
    };
    this.ngZone.run(apply);
    requestAnimationFrame(() => {
      if (gen !== this.liftScheduleGeneration) {
        return;
      }
      this.ngZone.run(apply);
      requestAnimationFrame(() => {
        if (gen !== this.liftScheduleGeneration) {
          return;
        }
        this.ngZone.run(apply);
      });
    });
    this.pendingLiftTimeouts.push(
      setTimeout(() => this.ngZone.run(apply), 72),
      setTimeout(() => this.ngZone.run(apply), 160)
    );
  }

  /** True when software keyboard is probably covering the bottom of the screen (native + visualViewport). */
  isKeyboardLikelyVisible(): boolean {
    if (this.keyboardHeight > 60) {
      return true;
    }
    if (typeof window !== 'undefined' && window.visualViewport) {
      const vv = window.visualViewport;
      const inferred =
        window.innerHeight - vv.height - Math.max(0, vv.offsetTop);
      return inferred > 110;
    }
    return false;
  }

  /** Call when auth sheets close so wrappers are not left translated. */
  clearViewportLiftForContainerIds(containerIds: string[]): void {
    this.clearPendingLiftSchedules();
    this.liftScheduleGeneration++;
    for (const id of containerIds) {
      const el = document.getElementById(id);
      if (el) {
        el.style.transform = '';
        el.style.transition =
          'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
      this.activeTransforms.delete(id);
    }
  }

  private checkAndApplyTransform(input: HTMLElement): void {
    const matchingConfig = this.findMatchingConfig(input);
    if (!matchingConfig) {
      return;
    }

    const containerElement = document.getElementById(matchingConfig.containerId);
    if (!containerElement) {
      return;
    }

    if (matchingConfig.useViewportLift) {
      if (!this.effectiveKeyboardHeight()) {
        return;
      }
      this.applyViewportLift(input, containerElement);
    } else {
      containerElement.style.transform = matchingConfig.transform;
      containerElement.style.transition = 'transform 0.2s ease';
      this.activeTransforms.set(matchingConfig.containerId, matchingConfig.transform);
    }
  }

  private handleKeyboardShow(info: KeyboardInfo): void {
    this.ngZone.run(() => {
      if (this.pendingHideClear) {
        clearTimeout(this.pendingHideClear);
        this.pendingHideClear = null;
      }
      this.keyboardHeight = info.keyboardHeight;
      this.lastKeyboardHeight = info.keyboardHeight;

      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        this.liftForFocusedField(activeElement);
      }
    });
  }

  private handleKeyboardHide(): void {
    if (this.pendingHideClear) {
      clearTimeout(this.pendingHideClear);
    }
    this.pendingHideClear = setTimeout(() => {
      this.ngZone.run(() => {
        this.pendingHideClear = null;
        // Spurious hide while jumping fields often leaves keyboard visually present
        let keyboardLikelyStillOpen = false;
        if (typeof window !== 'undefined' && window.visualViewport) {
          const vv = window.visualViewport;
          const inferred =
            window.innerHeight - vv.height - Math.max(0, vv.offsetTop);
          keyboardLikelyStillOpen = inferred > 100;
        }
        if (keyboardLikelyStillOpen) {
          const ae = document.activeElement as HTMLElement | null;
          if (
            ae &&
            (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')
          ) {
            this.liftForFocusedField(ae);
          }
          return;
        }

        this.clearPendingLiftSchedules();
        this.liftScheduleGeneration++;

        this.keyboardHeight = 0;

        this.activeTransforms.forEach((_, containerId) => {
          const container = document.getElementById(containerId);
          if (container) {
            container.style.transform = '';
            container.style.transition = 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          }
        });

        this.activeTransforms.clear();
      });
    }, 220);
  }

  async destroy(): Promise<void> {
    this.clearPendingLiftSchedules();
    if (this.visualViewportRaf !== null) {
      cancelAnimationFrame(this.visualViewportRaf);
      this.visualViewportRaf = null;
    }
    if (this.pendingHideClear) {
      clearTimeout(this.pendingHideClear);
      this.pendingHideClear = null;
    }
    if (typeof window !== 'undefined' && window.visualViewport && this.visualViewportResizeListener) {
      window.visualViewport.removeEventListener(
        'resize',
        this.visualViewportResizeListener
      );
      window.visualViewport.removeEventListener(
        'scroll',
        this.visualViewportResizeListener
      );
      this.visualViewportResizeListener = null;
    }
    if (this.isKeyboardAvailable) {
      if (this.keyboardShowListener) {
        await this.keyboardShowListener.remove();
        this.keyboardShowListener = null;
      }
      if (this.keyboardHideListener) {
        await this.keyboardHideListener.remove();
        this.keyboardHideListener = null;
      }
    }

    this.isInitialized = false;
    this.isKeyboardAvailable = false;
    this.activeTransforms.clear();
  }
}
